import React, { useState, useEffect, useCallback } from 'react';
import { fetchCihazlar } from '../services/istatistikService';
import { fetchDevicesByCustomer } from '../services/devicesService';
import TableListFooter from '../components/TableListFooter';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatGaranti = (device) => {
  if (!device) return '—';
  const ay = device.garantiSuresi;
  const bitis = device.garantiBitisTarihi;
  if (ay != null && ay > 0) return `${ay} ay`;
  if (bitis) {
    try {
      const d = new Date(bitis);
      return d.toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' });
    } catch {
      return '—';
    }
  }
  return '—';
};

const IstatistikCihazlarPage = () => {
  const [musteriler, setMusteriler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [cihazlarCache, setCihazlarCache] = useState({});
  const [cihazlarLoading, setCihazlarLoading] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const effectiveArama = debouncedSearch.length >= 3 ? debouncedSearch : undefined;

  useEffect(() => setCurrentPage(1), [debouncedSearch]);

  useEffect(() => {
    if (paginationMeta.totalPages >= 1 && currentPage > paginationMeta.totalPages) {
      setCurrentPage(paginationMeta.totalPages);
    }
  }, [paginationMeta.totalPages, currentPage]);

  const loadMusteriler = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchCihazlar({
        sayfa: currentPage,
        limit: PAGE_SIZE,
        arama: effectiveArama,
      });
      if (response?.basarili && response?.veri) {
        setMusteriler(response.veri.musteriler || []);
        const p = response.veri.sayfalama || {};
        setPaginationMeta({
          total: p.toplamKayit ?? 0,
          totalPages: Math.max(1, p.toplamSayfa ?? 1),
        });
      } else {
        setMusteriler([]);
        setPaginationMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Liste yüklenemedi'));
      setMusteriler([]);
      setPaginationMeta({ total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [effectiveArama, currentPage]);

  useEffect(() => {
    loadMusteriler();
  }, [loadMusteriler]);

  const loadCihazlar = useCallback(async (musteriId) => {
    if (cihazlarCache[musteriId]) return;
    setCihazlarLoading(musteriId);
    try {
      const response = await fetchDevicesByCustomer(musteriId, { page: 1, limit: 100 });
      if (response?.basarili && response?.veri?.cihazlar) {
        setCihazlarCache((prev) => ({ ...prev, [musteriId]: response.veri.cihazlar }));
      } else {
        setCihazlarCache((prev) => ({ ...prev, [musteriId]: [] }));
      }
    } catch {
      setCihazlarCache((prev) => ({ ...prev, [musteriId]: [] }));
    } finally {
      setCihazlarLoading(null);
    }
  }, [cihazlarCache]);

  const toggleExpand = (musteriId) => {
    setExpandedRow((prev) => (prev === musteriId ? null : musteriId));
    if (expandedRow !== musteriId) loadCihazlar(musteriId);
  };

  return (
    <main className="main-content istatistik-cihazlar-page">
      <div className="content-header">
        <h1>Cihazlar</h1>
      </div>

      <div className="admin-finance-toolbar">
        <div className="admin-finance-search-row">
          <div className="search-box admin-finance-search-box">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Ürün adı, ürün kodu veya müşteri adı (en az 3 karakter)…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              maxLength={80}
              aria-label="Ara"
            />
            {loading && searchInput.trim().length >= 3 && (
              <i className="fas fa-spinner fa-spin admin-finance-search-spinner" aria-hidden="true"></i>
            )}
          </div>
        </div>
        <p className="admin-finance-search-hint">
          Arama en az 3 karakter sonrası uygulanır. Sayfa başına en fazla {PAGE_SIZE} kayıt. En fazla cihazı olan müşteri en üstte.
        </p>
      </div>

      <div className="card">
        {loading && musteriler.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Liste yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : musteriler.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-box" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Müşteri bulunamadı</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Müşteri</th>
                    <th>Cihaz Sayısı</th>
                  </tr>
                </thead>
                <tbody>
                  {musteriler.map((m, idx) => {
                    const isExpanded = expandedRow === m.musteriId;
                    const cihazlar = cihazlarCache[m.musteriId];
                    const loadingCihazlar = cihazlarLoading === m.musteriId;
                    return (
                      <React.Fragment key={m.musteriId || idx}>
                        <tr
                          className={isExpanded ? 'expanded-row' : ''}
                          onClick={() => toggleExpand(m.musteriId)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: 'var(--text-gray)' }}></i>
                          </td>
                          <td style={{ fontWeight: 500 }}>{m.musteriAdi || '—'}</td>
                          <td>{m.cihazSayisi} ürün</td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={3} style={{ padding: 0, verticalAlign: 'top', backgroundColor: 'var(--bg-light)' }}>
                              <div style={{ padding: '12px 16px' }}>
                                {loadingCihazlar ? (
                                  <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-gray)' }}>
                                    <i className="fas fa-spinner fa-spin"></i> Yükleniyor…
                                  </div>
                                ) : Array.isArray(cihazlar) && cihazlar.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                                    {cihazlar.map((c, i) => (
                                      <li key={c.id || i} style={{ marginBottom: 6 }}>
                                        <strong>{c.icerik?.icerikAdi || c.urun?.urunAdi || '—'}</strong>
                                        {c.seriNumarasi && ` • Seri: ${c.seriNumarasi}`}
                                        {' • Garanti: '}
                                        {formatGaranti(c)}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Cihaz bulunamadı</div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>
    </main>
  );
};

export default IstatistikCihazlarPage;
